import React, { Fragment, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import IconWrapper from '../../IconWrapper';
import { FiMoreVertical, FiRefreshCw, FiExternalLink, FiInfo, FiCheckCircle } from 'react-icons/fi';
import { Signal } from '../../../types/signals-live';
import { Timestamp } from '../../ui/timestamp';

interface SignalAdminMenuProps {
  signal: Signal;
  onReclassify?: (signalId: string) => Promise<void>;
}

const SignalAdminMenu: React.FC<SignalAdminMenuProps> = ({ signal, onReclassify }) => {
  const [isReclassifying, setIsReclassifying] = useState(false);
  const [reclassifySuccess, setReclassifySuccess] = useState(false);

  const handleReclassify = async () => {
    if (!onReclassify || isReclassifying) return;
    
    setIsReclassifying(true);
    setReclassifySuccess(false);
    
    try {
      await onReclassify(signal.id);
      setReclassifySuccess(true);
      setTimeout(() => setReclassifySuccess(false), 3000);
    } catch (error) {
      console.error('Failed to reclassify signal:', error);
    } finally {
      setIsReclassifying(false);
    }
  };

  const handleViewInTaxonomy = () => {
    // Navigate to taxonomy view with signal context
    const taxonomyUrl = `/v3/taxonomy?signal=${signal.id}`;
    window.open(taxonomyUrl, '_blank');
  };

  // Extract taxonomy version from classification
  // The taxonomy_classification might be passed separately or as part of the signal data
  const taxonomyVersion = (signal as any).taxonomy_classification?.taxonomy_version || 
                          signal.classification?.model_version || 
                          'Unknown';

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-go-primary">
          <IconWrapper icon={FiMoreVertical} className="w-4 h-4" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-64 origin-top-right divide-y divide-gray-100 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          {/* Taxonomy Info Section */}
          <div className="px-4 py-3">
            <div className="flex items-start gap-2">
              <IconWrapper icon={FiInfo} className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-gray-500">Taxonomy Version</p>
                <p className="text-sm text-gray-900 truncate">{taxonomyVersion}</p>
              </div>
            </div>
          </div>

          {/* Actions Section */}
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleReclassify}
                  disabled={isReclassifying}
                  className={`${
                    active ? 'bg-gray-50' : ''
                  } group flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {reclassifySuccess ? (
                    <>
                      <IconWrapper icon={FiCheckCircle} className="w-4 h-4 text-green-500" />
                      <span className="text-green-700">Reclassification triggered</span>
                    </>
                  ) : (
                    <>
                      <IconWrapper 
                        icon={FiRefreshCw} 
                        className={`w-4 h-4 text-gray-400 group-hover:text-gray-600 ${isReclassifying ? 'animate-spin' : ''}`} 
                      />
                      <div className="flex-1 text-left">
                        <div className="text-gray-700 group-hover:text-gray-900">
                          {isReclassifying ? 'Reclassifying...' : 'Reclassify Signal'}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          Re-run AI classification with latest model
                        </div>
                      </div>
                    </>
                  )}
                </button>
              )}
            </Menu.Item>

            <Menu.Item>
              {({ active }) => (
                <button
                  onClick={handleViewInTaxonomy}
                  className={`${
                    active ? 'bg-gray-50' : ''
                  } group flex w-full items-center gap-3 px-4 py-2.5 text-sm transition-colors duration-150`}
                >
                  <IconWrapper icon={FiExternalLink} className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
                  <div className="flex-1 text-left">
                    <div className="text-gray-700 group-hover:text-gray-900">
                      View in Taxonomy Navigator
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Explore classification in detail
                    </div>
                  </div>
                </button>
              )}
            </Menu.Item>
          </div>

          {/* Classification Metadata */}
          {signal.classification && (
            <div className="px-4 py-3">
              <div className="text-xs text-gray-500 space-y-1">
                {signal.classification.created_at && (
                  <div>
                    Classified: <Timestamp value={signal.classification.created_at} format="date-only" />
                  </div>
                )}
                {signal.classification.langsmith_trace_id && (
                  <div className="truncate" title={signal.classification.langsmith_trace_id}>
                    Trace: {signal.classification.langsmith_trace_id.slice(0, 8)}...
                  </div>
                )}
              </div>
            </div>
          )}
        </Menu.Items>
      </Transition>
    </Menu>
  );
};

export default SignalAdminMenu;